import { Alert, Autocomplete, Box, Stack, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { fetchDirectCalendar } from "./DirectCalendarApi";
import DirectCalendarSection from "./DirectCalendarSection";
import CalendarDateNav from "./CalendarDateNav";

export default function DirectCalendarWidget({
  widgetConfigurationId,
  date,
  discipline = null,
  competitionIds = null,
  hasCompetitionIdsParam = false,
  reloadKey = 0,
  onCompetitionIdsChange,
  onDateChange,
  onNavigate,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const [localDate, setLocalDate] = useState(null);
  const [localCompetitionIds, setLocalCompetitionIds] = useState([]);

  const [localHasCompetitionIdsParam, setLocalHasCompetitionIdsParam] = useState(false);

  const effectiveDate = date || localDate;

  const isCompetitionControlled = Array.isArray(competitionIds);

  const effectiveCompetitionIds = isCompetitionControlled
    ? competitionIds
    : localCompetitionIds;

  const effectiveHasCompetitionIdsParam = isCompetitionControlled
    ? hasCompetitionIdsParam
    : localHasCompetitionIdsParam;

  const competitionIdsKey = effectiveCompetitionIds.join(',');

  const handleDateChange = (nextDate) => {
    if (onDateChange) {
      onDateChange(nextDate);
      return;
    }

    setLocalDate(nextDate);
  };

  const handleCompetitionChange = (_, value) => {
  const nextIds = Array.isArray(value)
    ? value.map((competition) => String(competition._id)).filter(Boolean)
    : [];

  setLocalDate(null);

  if (onCompetitionIdsChange) {
    onCompetitionIdsChange(nextIds);
    return;
  }

  setLocalHasCompetitionIdsParam(true);
  setLocalCompetitionIds(nextIds);
};

  useEffect(() => {
  let ignore = false;

  const load = async () => {
    if (!widgetConfigurationId) {
      setLoading(false);
      setError('Configuration widget manquante.');
      setData(null);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const result = await fetchDirectCalendar({
        widgetConfigurationId,
        date: effectiveDate,
        discipline,
        competitionIds: effectiveCompetitionIds,
        hasCompetitionIdsParam: effectiveHasCompetitionIdsParam,
      });

      if (!ignore) {
        setData(result);
      }
    } catch {
      if (!ignore) {
        setError('Impossible de charger le calendrier.');
      }
    } finally {
      if (!ignore) {
        setLoading(false);
      }
    }
  };

  load();

  return () => {
    ignore = true;
  };
}, [widgetConfigurationId, effectiveDate, discipline, competitionIdsKey, effectiveHasCompetitionIdsParam, reloadKey]);

useEffect(() => {
  setLocalDate(null);

  if (!isCompetitionControlled) {
    setLocalCompetitionIds([]);
    setLocalHasCompetitionIdsParam(false);
  }
}, [discipline, isCompetitionControlled]);

  if (loading) return null;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;
  const groups = Array.isArray(data?.items) ? data.items : [];

  const hasCompetitionData = (competition) =>
    Array.isArray(competition?.matches) && competition.matches.length > 0;

  const visibleGroups = groups
    .map((group) => ({
      ...group,
      competitions: Array.isArray(group?.competitions)
        ? group.competitions.filter(hasCompetitionData)
        : [],
    }))
    .filter((group) => group.competitions.length > 0);

  const showDisciplineTitle = visibleGroups.length > 1;
  const hasVisibleMatches = visibleGroups.length > 0;

  const competitionOptions = Array.isArray(data?.competitionOptions)
  ? data.competitionOptions
  : [];

  const backendSelectedCompetitionIds = Array.isArray(data?.selectedCompetitionIds)
    ? data.selectedCompetitionIds.map((id) => String(id))
    : [];

  const displayedCompetitionIds =
  effectiveCompetitionIds.length > 0
    ? effectiveCompetitionIds
    : !effectiveHasCompetitionIdsParam
    ? backendSelectedCompetitionIds
    : [];

  const selectedCompetitionOptions = competitionOptions.filter((competition) =>
    displayedCompetitionIds.includes(String(competition._id))
  );

  const showCompetitionInput = competitionOptions.length > 1;

  const getCompetitionLabel = (competition) => {
    if (!competition) return '';

    const name = competition.name || '';
    const season = competition.season || '';

    return season ? `${name} - ${season}` : name;
  };

  return (
    <Box sx={{ width: '100%', bgcolor: '#fff' }}>
      {showCompetitionInput ? (
        <Autocomplete
          multiple
          options={competitionOptions}
          value={selectedCompetitionOptions}
          onChange={handleCompetitionChange}
          getOptionLabel={getCompetitionLabel}
          isOptionEqualToValue={(option, value) =>
            String(option._id) === String(value._id)
          }
          sx={{ mb: 2 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Compétitions"
              placeholder="Toutes les compétitions"
              helperText="Filtre les matchs et les dates disponibles du calendrier."
            />
          )}
        />
      ) : null}
      <CalendarDateNav
        selectedDate={data.selectedDate}
        previousDate={data.previousDate}
        nextDate={data.nextDate}
        previousDates={data.previousDates || []}
        nextDates={data.nextDates || []}
        availableDates={data.availableDates || []}
        onDateChange={handleDateChange}
      />
      {!data.selectedDate ? (
        <Alert severity="info" sx={{ mx: 2, my: 2 }}>
          Aucun match disponible sur la période configurée.
        </Alert>
      ) : null}

     {data.selectedDate && !hasVisibleMatches ? (
  <Alert severity="info" sx={{ mx: 2, my: 2 }}>
    Aucun match prévu pour cette date.
  </Alert>
) : null}

    {hasVisibleMatches ? (
      <Stack spacing={3}>
        {visibleGroups.map((disciplineGroup) => (
          <DirectCalendarSection
            key={disciplineGroup.discipline}
            group={disciplineGroup}
            showDisciplineTitle={showDisciplineTitle}
            onNavigate={onNavigate}
          />
        ))}
      </Stack>
    ) : null}
    </Box>
  );
}